import { styled } from '@mui/material';
import type { MouseEvent, ReactNode } from 'react';

import { NextIcon, TopicIcon } from '../../../icons';
import { Tag } from '../../atoms/Tag';

const DEFAULT_ARCHIVED_LABEL = 'Archivé';
const SELECTED_TINT_OPACITY = 0.08;
const SELECTED_TILE_TINT_OPACITY = 0.12;

export const TOPIC_TILE_SIZE = 32;

type Props = {
  name: string;
  href: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /** Short summary under the name, e.g. "4 sous-sujets actifs". */
  description?: string;
  /** The topic whose sub-topics are shown next to the list. */
  selected?: boolean;
  archived?: boolean;
  archivedLabel?: string;
  /** Secondary controls (resource menu). Rendered above the row link, never nested in it. */
  action?: ReactNode;
  'data-testid'?: string;
};

type OwnerState = {
  selected: boolean;
  archived: boolean;
};

const Row = styled('div')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: `${TOPIC_TILE_SIZE}px minmax(0, 1fr) auto`,
  gridTemplateAreas: `
    "tile name trailing"
    "tile meta trailing"`,
  columnGap: theme.spacing(1.5),
  alignItems: 'center',
  minHeight: theme.spacing(8),
  paddingBlock: theme.spacing(1),
  paddingInline: theme.spacing(2),
  backgroundColor: ownerState.selected
    ? theme.alpha(theme.palette.primary.main, SELECTED_TINT_OPACITY)
    : 'transparent',
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: ownerState.selected
        ? theme.alpha(theme.palette.primary.main, SELECTED_TILE_TINT_OPACITY)
        : theme.palette.action.hover,
    },
  },
  '&:has(a:focus-visible)': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
}));

const Tile = styled('span')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  gridArea: 'tile',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: TOPIC_TILE_SIZE,
  height: TOPIC_TILE_SIZE,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: ownerState.selected
    ? theme.alpha(theme.palette.primary.main, SELECTED_TILE_TINT_OPACITY)
    : theme.palette.background.elevation1,
  color: ownerState.selected ? theme.palette.primary.main : theme.palette.text.secondary,
}));

const NameLink = styled('a')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  ...theme.typography.subtitle1,
  gridArea: 'name',
  minWidth: 0,
  color: ownerState.selected
    ? theme.palette.primary.main
    : ownerState.archived
      ? theme.palette.text.secondary
      : theme.palette.text.primary,
  textDecoration: 'none',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
  },
  '&:focus-visible': {
    outline: 'none',
  },
}));

const Meta = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  gridArea: 'meta',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 0,
  color: theme.palette.text.secondary,
  fontVariantNumeric: 'tabular-nums',
}));

const Description = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const Trailing = styled('span')(({ theme }) => ({
  gridArea: 'trailing',
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.secondary,
}));

const ActionSlot = styled('span')(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  marginRight: theme.spacing(-1),
}));

/**
 * One topic of the topics screen, inside a `TopicList`.
 * The name is the row link (its hit area covers the whole row) and opens the topic's sub-topics;
 * the selected topic is tinted and announced as the current page. The optional action sits above
 * the link so the resource menu stays a separate control.
 */
export function TopicListItem(props: Props) {
  const ownerState = { selected: Boolean(props.selected), archived: Boolean(props.archived) };
  const hasMeta = Boolean(props.description) || ownerState.archived;

  return (
    <li data-testid={props['data-testid'] ?? 'topic-list-item'}>
      <Row ownerState={ownerState}>
        <Tile aria-hidden="true" ownerState={ownerState}>
          <TopicIcon fontSize="small" />
        </Tile>
        <NameLink
          href={props.href}
          onClick={props.onClick}
          aria-current={ownerState.selected ? 'page' : undefined}
          ownerState={ownerState}
        >
          {props.name}
        </NameLink>
        {hasMeta ? (
          <Meta>
            {ownerState.archived ? (
              <Tag label={props.archivedLabel ?? DEFAULT_ARCHIVED_LABEL} color="default" variantIcon="state" />
            ) : null}
            {props.description ? <Description>{props.description}</Description> : null}
          </Meta>
        ) : null}
        <Trailing>
          {props.action ? (
            <ActionSlot>{props.action}</ActionSlot>
          ) : (
            <NextIcon fontSize="small" aria-hidden="true" />
          )}
        </Trailing>
      </Row>
    </li>
  );
}

export type { Props as TopicListItemProps };
